/**
 * Incidents IT › Commentaires : règles pures partagées par l'API, le front et le mode maquette.
 * Référence fonctionnelle : ReFresh US #79 (F-14) – un agent choisit, pour chaque message, une note interne (invisible
 * du demandeur) ou une réponse visible du demandeur ; le demandeur répond toujours de façon visible. Un commentaire est
 * immuable (ni modification ni suppression) : sa correction s'ajoute au fil (it/corrections.ts, D-IT-036) ; son texte
 * n'est jamais copié dans l'audit ni dans une notification.
 *
 * Un commentaire n'est pas une action du cycle de vie (ACTIONS_INCIDENT). Seule exception (décision du 27/10/2026) : la
 * réponse du demandeur sur un incident en attente le fait repasser en traitement (repriseParReponse). Un incident
 * clôturé ou archivé ne se commente plus (commentaireFerme) : le demandeur rouvre l'incident clôturé pendant le délai.
 */
import { enregistrerPremiereReponse, type RefusActeurIncident, type StatutIncident } from './incidents';

/**
 * Le fil d'un incident est fermé : `archive` (plus aucune écriture) ou `cloture` (décision du 27/10/2026 : le demandeur
 * doit d'abord rouvrir l'incident) ; `null` s'il est ouvert. L'API et la maquette refusent alors (409 etat_incompatible).
 */
export function commentaireFerme(incident: { statut: StatutIncident; archiveLe: string | null }): 'archive' | 'cloture' | null {
  if (incident.archiveLe) return 'archive';
  return incident.statut === 'cloture' ? 'cloture' : null;
}

/**
 * La réponse relance l'incident (décision du 27/10/2026) : réponse publique du DEMANDEUR sur un incident en attente, qui
 * repasse en traitement. Ni une note interne, ni un agent, ni le déclarant pour un tiers ne déclenchent la reprise.
 */
export function repriseParReponse(
  commentaire: { interne: boolean },
  incident: { demandeur: string; statut: StatutIncident },
  moi: string,
): boolean {
  return !commentaire.interne && incident.demandeur === moi && incident.statut === 'en-attente';
}

/**
 * Agent affecté à notifier quand un collègue écrit sur son incident (décision du 27/10/2026), réponse publique comme
 * note interne : `null` pour ses propres messages, sans agent affecté, ou pour un message qui n'est pas d'un agent.
 * Jamais quand l'agent affecté serait le demandeur : cas impossible depuis D-IT-124 (contrainte
 * incident_agent_non_demandeur), garde conservée pour qu'il n'apprenne jamais l'existence d'une note interne.
 */
export function agentAffecteANotifier(
  incident: { demandeur: string; affecteA: string | null },
  moi: string,
  auteur: AuteurCommentaire,
): string | null {
  const a = incident.affecteA;
  return auteur === 'agent' && a !== null && a !== moi && a !== incident.demandeur ? a : null;
}

/**
 * Qui écrit sur un incident : `agent` (droit `modifier` sur la filiale de l'incident, sans en être le demandeur) ou
 * `participant` (le demandeur, ou le déclarant pour un tiers qui n'est pas agent).
 */
export type AuteurCommentaire = 'agent' | 'participant';

/**
 * Rôle de `moi` sur l'incident, ou `null` s'il ne peut pas commenter. Le demandeur reste participant même s'il est agent :
 * il ne lit ni n'écrit jamais de note interne sur son propre incident.
 *
 * Le déclarant pour un tiers commente comme le demandeur (D-IT-072). Les lecteurs d'un incident restreint sans
 * `modifier` (groupes CYBER, DSI) ne commentent pas : règle reportée au retour du marquage (D-IT-073, #232).
 */
export function auteurCommentaire(
  incident: { demandeur: string; declarant: string },
  moi: string,
  estAgent: boolean,
): AuteurCommentaire | null {
  if (incident.demandeur === moi) return 'participant';
  if (estAgent) return 'agent';
  return incident.declarant === moi ? 'participant' : null;
}

/**
 * Refus d'acteur d'un commentaire (403, mêmes messages que les actions) : `non-demandeur` pour qui n'est ni agent, ni
 * demandeur, ni déclarant ; `demandeur` pour une note interne écrite par un participant.
 */
export function refusCommentaire(
  commentaire: { interne: boolean },
  incident: { demandeur: string; declarant: string },
  moi: string,
  estAgent: boolean,
): RefusActeurIncident | null {
  const auteur = auteurCommentaire(incident, moi, estAgent);
  if (auteur === null) return 'non-demandeur';
  return commentaire.interne && auteur === 'participant' ? 'demandeur' : null;
}

/**
 * Un commentaire est lisible par `moi` : public, ou note interne lue par un agent. Une note interne n'est JAMAIS lisible
 * par le demandeur, même agent ou administrateur (la RLS de it.commentaire applique la même règle).
 */
export function commentaireLisible(
  commentaire: { interne: boolean },
  incident: { demandeur: string; declarant: string },
  moi: string,
  estAgent: boolean,
): boolean {
  return !commentaire.interne || auteurCommentaire(incident, moi, estAgent) === 'agent';
}

/**
 * Un commentaire pose la première réponse au demandeur : réponse publique d'un agent, tant qu'aucune première réponse
 * n'est enregistrée (prise en charge ou commentaire précédent). Ni une note interne, ni un message du demandeur ne
 * comptent. Renvoie la date à enregistrer, ou `null`.
 */
export function premiereReponseParCommentaire(
  commentaire: { interne: boolean },
  auteur: AuteurCommentaire,
  premiereReponseLe: Date | null,
  maintenant: Date,
): Date | null {
  if (commentaire.interne || auteur !== 'agent') return null;
  const r = enregistrerPremiereReponse(premiereReponseLe, maintenant);
  return r.nouvelle ? r.premiereReponseLe : null;
}
